/**
 * CommunicationLogUI
 *
 * Emails and phone-call entries for one form version or entity record, plus
 * the controls that add, edit, delete and import them. Consumer:
 * `RenderUIElement`, for a `communicationLog` field.
 *
 * Its records come from an independent bundles subscription
 * (`useCommunicationLogBundles`), not from the parent document, so the only
 * parent state it needs is the attachments subtree and `splitQuery`. Both are
 * read as narrow leaves — `artifact.data?.attachments` through
 * `useCurrentArtifact`, `splitQuery` off the `formStore` snapshot — never
 * through `useFormStore`, which would re-render the whole log on every
 * keystroke elsewhere in the form (SYD-2252).
 *
 * Attachment reads and writes both narrow on `artifact.kind`, so the document
 * shown is always the document written.
 *
 * `handleLabelsSave` sends only the assignment, through `setRecordLabels` rather
 * than the record-replacement path the email and phone edits use: those callers
 * authored every field they send, this one changes one field of a row the backend
 * may settle in place at any moment. It stacks no debounce of its own, and
 * captures the ids it was given before the write, never re-reading a proxy the
 * listener echo may have moved on.
 *
 * `handleCheckOrderStatus` is the one handler here that persists nothing: it asks
 * Signify what it knows about an unresolved order attempt and hands the answer to
 * the row to display, leaving the record's outcome as it stands. It sends the
 * parent as validated segments (`signifyCheckParent`), never the `parentDocPath`
 * string the five persisting handlers use, because the callable authorizes that
 * parent server-side rather than trusting a path.
 *
 * `disabled` and `isPdfExportMode` reach the table separately: a read-only log
 * still shows its row actions, greyed, while a printed page has no buttons at all.
 */

import { FC, useCallback, useContext, useMemo, useRef } from 'react';
import { useSnapshot } from 'valtio';
import { Timestamp } from 'firebase/firestore';
import {
    ArtifactAttachments,
    AttachmentVersion,
    CheckSignifyOrderStatusParent,
    CommunicationRecord,
    EmailCommunicationRecord,
    LabelId,
    PhoneCallRecord,
    SignifyOrderStatusCheck,
} from '#types';
import { MAX_COMMUNICATION_LOG_ENTRIES } from '#types/System.Parameters.types';
import { RcFile } from 'antd/es/upload/interface';
import CommunicationTable from './components/CommunicationTable';
import LogTableControls from './components/LogTableControls';
import { EmailFormData } from './components/EmailPopoverForm';
import { PhoneCallFormData } from './components/PhoneCallPopoverForm';
import { sanitizeEmailBody } from '#types';
import {
    ParentUploadFunction,
    getEmailFingerprint,
} from './parser/communicationParser';
import { useAttachments } from '../../../../use/attachments/useAttachments';
import { MessageContext } from '../../../../providers/MessageContext';
import { formStore, useEntityRecordScope } from '../../../../store';
import { useCurrentArtifact } from '../../../../use/useCurrentArtifact';
import { useCommunicationLogLabels } from '../../../../use/useOrganizationParameters';
import { useCommunicationLogBundles } from './use/useCommunicationLogBundles';
import { checkSignifyOrderStatus } from '@/api/signify/services/signifyApiService';
import { CommunicationLogBundles } from './services/CommunicationLogBundles';
import FlexCol from '@/components/atoms/FlexCol';
import { usePdfExportContext } from '@/components/pdfExport/context/PdfExportContext';

type CommunicationLogDisplayMode = 'default' | 'compact';

interface CommunicationLogUIProps {
    disabled?: boolean;
    logdisplay?: CommunicationLogDisplayMode;
    [key: string]: any;
}

const CommunicationLogUI: FC<CommunicationLogUIProps> = ({
    disabled = false,
    logdisplay = 'default',
    ...props
}) => {
    const { isPdfExportMode } = usePdfExportContext();
    const { messageApi } = useContext(MessageContext);
    const artifact = useCurrentArtifact();
    const insideFormContext = artifact.insideFormContext;
    const insideEntityContext = artifact.insideEntityContext;
    const formId = artifact.kind === 'form' ? artifact.formId : undefined;
    const versionId = artifact.kind === 'form' ? artifact.versionId : undefined;
    const entityId = artifact.kind === 'entity' ? artifact.entityId : undefined;
    const entityRecordId = artifact.kind === 'entity' ? artifact.recordId : undefined;
    const splitQuery = useSnapshot(formStore.state).splitQuery;
    const communicationLogLabels = useCommunicationLogLabels();
    const scope = useEntityRecordScope();
    const selectedEntityRecord = scope.entityRecord.data;
    const debouncedSaveEntityRecord = scope.debouncedSetEntityRecord;

    const communicationLogFieldId = typeof props.id === 'string' && props.id.length > 0
        ? props.id
        : 'communicationLog';

    const formVersionId = versionId;

    const parentDocPath = useMemo(() => {
        if (insideFormContext && formId && formVersionId) {
            return `forms/${formId}/formVersions/${formVersionId}`;
        }
        if (insideEntityContext && entityId && entityRecordId) {
            return `entities/${entityId}/records/${entityRecordId}`;
        }
        return undefined;
    }, [entityId, entityRecordId, formId, formVersionId, insideEntityContext, insideFormContext]);

    const signifyCheckParent = useMemo((): CheckSignifyOrderStatusParent | undefined => {
        if (insideFormContext && formId && formVersionId) {
            return { kind: 'form', formId, versionId: formVersionId };
        }
        if (insideEntityContext && entityId && entityRecordId) {
            return { kind: 'entityRecord', entityId, entityRecordId };
        }
        return undefined;
    }, [entityId, entityRecordId, formId, formVersionId, insideEntityContext, insideFormContext]);

    const bundlesEnabled = Boolean(parentDocPath);

    const {
        bundledRecords,
        recordBundleDocIdByRecordId,
        loading: bundlesLoading,
    } = useCommunicationLogBundles({
        parentDocPath,
        fieldId: communicationLogFieldId,
        enabled: bundlesEnabled,
    });

    const records = useMemo(
        () => (bundlesEnabled ? bundledRecords : []),
        [bundlesEnabled, bundledRecords],
    );

    const recordCount = bundlesEnabled ? bundledRecords.length : 0;

    const persistenceDebugContext = useMemo(() => ({
        insideFormContext,
        insideEntityContext,
        formId,
        formVersionId,
        entityId,
        entityRecordId,
        parentDocPath,
        bundlesEnabled,
        bundlesLoading,
        fieldId: communicationLogFieldId,
    }), [
        bundlesEnabled,
        bundlesLoading,
        communicationLogFieldId,
        entityId,
        entityRecordId,
        formId,
        formVersionId,
        insideEntityContext,
        insideFormContext,
        parentDocPath,
    ]);

    const communicationLogLoadingMessage = 'Communication Log is still loading. Please wait a moment and try again.';

    const buildUnsavedParentMessage = useCallback((actionLabel: string) => {
        return insideEntityContext
            ? `Cannot ${actionLabel} yet. Save the record first, then try again.`
            : `Cannot ${actionLabel} yet. Save the form first, then try again.`;
    }, [insideEntityContext]);

    const reportBlockedCommunicationLogAction = useCallback((params: {
        logLabel: string;
        userMessage: string;
        toast: 'error' | 'warning';
        recordId?: string;
        bundleDocId?: string;
    }) => {
        const { toast, userMessage, logLabel, recordId, bundleDocId } = params;

        if (toast === 'warning') {
            messageApi.warning(userMessage);
        } else {
            messageApi.error(userMessage);
        }

        console.error(`CommunicationLogUI: blocked ${logLabel}`, {
            ...persistenceDebugContext,
            userMessage,
            recordId,
            bundleDocId,
        });
    }, [messageApi, persistenceDebugContext]);

    const ensureCommunicationLogBundlesReady = useCallback((params: {
        actionLabel: string;
        logLabel: string;
        recordId?: string;
    }): { ok: true; parentDocPath: string } | { ok: false } => {
        const { actionLabel, logLabel, recordId } = params;

        if (!bundlesEnabled || !parentDocPath) {
            reportBlockedCommunicationLogAction({
                logLabel: `${logLabel} (missing parentDocPath)`,
                userMessage: buildUnsavedParentMessage(actionLabel),
                toast: 'error',
                recordId,
            });
            return { ok: false };
        }

        if (bundlesLoading) {
            reportBlockedCommunicationLogAction({
                logLabel: `${logLabel} (bundlesLoading)`,
                userMessage: communicationLogLoadingMessage,
                toast: 'warning',
                recordId,
            });
            return { ok: false };
        }

        return { ok: true, parentDocPath };
    }, [
        buildUnsavedParentMessage,
        bundlesEnabled,
        bundlesLoading,
        communicationLogLoadingMessage,
        parentDocPath,
        reportBlockedCommunicationLogAction,
    ]);

    const filteredRecords = useMemo(() => {
        if (!splitQuery) {
            return records;
        }
        const path = splitQuery.splitRef?.valuePath;
        if (!path) {
            return records;
        }
        return records.filter((record) => {
            const recordValue = (record as any)[path];
            return recordValue === splitQuery.queryValue;
        });
    }, [records, splitQuery]);

    const existingEmailFingerprints = useMemo(() => {
        const fingerprints = new Set<string>();
        for (const record of records) {
            if (record.type === 'email') {
                fingerprints.add(getEmailFingerprint(record));
            }
        }
        return fingerprints;
    }, [records]);

    const pendingEmailFingerprints = useRef(new Set<string>());

    const parentContextAttachments = artifact.kind === 'none' ? null : artifact.data?.attachments;

    const {
        uploadFiles: parentUploadFilesFromHook,
        removeFile: parentRemoveFileFromHook,
        removeVersion: parentRemoveVersionFromHook,
        artifactPath: parentHookArtifactPath,
    } = useAttachments({
        value: parentContextAttachments || {},
        onChange: (newParentAttachments: ArtifactAttachments) => {
            if (artifact.kind === 'form') {
                void formStore.getFormVersionPersist()?.setMerge(
                    { attachments: newParentAttachments },
                    { debounce: false },
                );
            } else if (artifact.kind === 'entity' && debouncedSaveEntityRecord) {
                debouncedSaveEntityRecord({ attachments: newParentAttachments });
            } else {
                console.warn('CommunicationLogUI: Cannot update parent attachments, context missing.');
            }
        },
        onError: (err) => {
            console.error('CommunicationLogUI: Parent attachment error:', err);
            messageApi.error(`Attachment error: ${err.message}`);
        },
        onSuccess: (msg) => {
            messageApi.success(msg);
        },
    });

    const handleEmailSave = useCallback(async (record: CommunicationRecord | null, formData: EmailFormData): Promise<boolean> => {
        const now = Timestamp.now();
        const baseRecord = record && record.type === 'email' ? record : null;

        if (!baseRecord && recordCount >= MAX_COMMUNICATION_LOG_ENTRIES) {
            messageApi.error(`Maximum communication log entries reached (${MAX_COMMUNICATION_LOG_ENTRIES}). Please delete some entries before adding new ones.`);
            return false;
        }

        const attachments = baseRecord?.attachments || {};
        const { body: sanitizedBody } = sanitizeEmailBody(formData.body);
        const updatedRecord: EmailCommunicationRecord = {
            type: 'email',
            id: baseRecord?.id || crypto.randomUUID(),
            key: baseRecord?.key || crypto.randomUUID(),
            from: formData.from || '',
            to: formData.to || [],
            cc: formData.cc,
            bcc: formData.bcc,
            subject: formData.subject || '',
            body: sanitizedBody,
            timestamp: baseRecord?.timestamp || now,
            hasAttachments: baseRecord?.hasAttachments
                || Object.keys(attachments).length > 0
                || (baseRecord?.outboundAttachmentLabels?.length ?? 0) > 0,
            attachments,
            outboundAttachmentLabels: baseRecord?.outboundAttachmentLabels,
            labelIds: baseRecord?.labelIds,
            headers: baseRecord?.headers,
            bodyAttachment: baseRecord?.bodyAttachment,
            createdAt: baseRecord?.createdAt || now,
            updatedAt: now,
            tag: formData.tag,
            contactEntityRecordId: formData.contactEntityRecordId,
        };

        const ready = ensureCommunicationLogBundlesReady({
            actionLabel: 'save email',
            logLabel: 'save email',
            recordId: updatedRecord.id,
        });
        if (!ready.ok) {
            return false;
        }

        const readyParentDocPath = ready.parentDocPath;
        const bundleDocId = recordBundleDocIdByRecordId.get(updatedRecord.id);
        try {
            if (bundleDocId) {
                await CommunicationLogBundles.replaceRecord({
                    parentDocPath: readyParentDocPath,
                    bundleDocId,
                    record: updatedRecord,
                });
                messageApi.success('Email updated.');
            } else {
                await CommunicationLogBundles.appendRecord({
                    parentDocPath: readyParentDocPath,
                    fieldId: communicationLogFieldId,
                    record: updatedRecord,
                });
                messageApi.success('Email added.');
            }
            return true;
        } catch (error) {
            console.error('CommunicationLogUI: Failed saving bundled email record:', {
                error,
                ...persistenceDebugContext,
                recordId: updatedRecord.id,
                bundleDocId,
            });
            messageApi.error(baseRecord ? 'Failed to update email.' : 'Failed to add email.');
            return false;
        }
    }, [
        communicationLogFieldId,
        ensureCommunicationLogBundlesReady,
        messageApi,
        persistenceDebugContext,
        recordCount,
        recordBundleDocIdByRecordId,
    ]);

    const handleAddEmail = useCallback((data: EmailFormData) => {
        return handleEmailSave(null, data);
    }, [handleEmailSave]);

    const handleUpdateEmail = useCallback((record: CommunicationRecord, data: EmailFormData) => {
        return handleEmailSave(record, data);
    }, [handleEmailSave]);

    const handlePhoneSave = useCallback(async (record: CommunicationRecord | null, formData: PhoneCallFormData): Promise<boolean> => {
        const now = Timestamp.now();
        const baseRecord = record && record.type === 'phone' ? record : null;

        if (!baseRecord && recordCount >= MAX_COMMUNICATION_LOG_ENTRIES) {
            messageApi.error(`Maximum communication log entries reached (${MAX_COMMUNICATION_LOG_ENTRIES}). Please delete some entries before adding new ones.`);
            return false;
        }

        const updatedRecord: PhoneCallRecord = {
            type: 'phone',
            id: baseRecord?.id || crypto.randomUUID(),
            key: baseRecord?.key || crypto.randomUUID(),
            timestamp: formData.timestamp,
            direction: formData.direction,
            phoneNumber: formData.phoneNumber,
            notes: formData.notes,
            tag: formData.tag,
            contactEntityRecordId: formData.contactEntityRecordId,
            labelIds: baseRecord?.labelIds,
            createdAt: baseRecord?.createdAt || now,
            updatedAt: now,
        };

        const ready = ensureCommunicationLogBundlesReady({
            actionLabel: 'save phone call',
            logLabel: 'save phone call',
            recordId: updatedRecord.id,
        });
        if (!ready.ok) {
            return false;
        }

        const readyParentDocPath = ready.parentDocPath;
        const bundleDocId = recordBundleDocIdByRecordId.get(updatedRecord.id);
        try {
            if (bundleDocId) {
                await CommunicationLogBundles.replaceRecord({
                    parentDocPath: readyParentDocPath,
                    bundleDocId,
                    record: updatedRecord,
                });
                messageApi.success('Phone call updated.');
            } else {
                await CommunicationLogBundles.appendRecord({
                    parentDocPath: readyParentDocPath,
                    fieldId: communicationLogFieldId,
                    record: updatedRecord,
                });
                messageApi.success('Phone call added.');
            }
            return true;
        } catch (error) {
            console.error('CommunicationLogUI: Failed saving bundled phone record:', {
                error,
                ...persistenceDebugContext,
                recordId: updatedRecord.id,
                bundleDocId,
            });
            messageApi.error(baseRecord ? 'Failed to update phone call.' : 'Failed to add phone call.');
            return false;
        }
    }, [
        communicationLogFieldId,
        ensureCommunicationLogBundlesReady,
        messageApi,
        persistenceDebugContext,
        recordCount,
        recordBundleDocIdByRecordId,
    ]);

    const handleAddPhone = useCallback((data: PhoneCallFormData) => {
        return handlePhoneSave(null, data);
    }, [handlePhoneSave]);

    const handleUpdatePhone = useCallback((record: CommunicationRecord, data: PhoneCallFormData) => {
        return handlePhoneSave(record, data);
    }, [handlePhoneSave]);

    const handleLabelsSave = useCallback(async (
        record: CommunicationRecord,
        labelIds: LabelId[],
    ): Promise<boolean> => {
        const ready = ensureCommunicationLogBundlesReady({
            actionLabel: 'change labels on this entry',
            logLabel: 'save labels',
            recordId: record.id,
        });
        if (!ready.ok) {
            return false;
        }

        const readyParentDocPath = ready.parentDocPath;
        const bundleDocId = recordBundleDocIdByRecordId.get(record.id);
        if (!bundleDocId) {
            reportBlockedCommunicationLogAction({
                logLabel: 'save labels (missing bundleDocId)',
                userMessage: 'Cannot change labels on this entry yet. Please refresh and try again.',
                toast: 'error',
                recordId: record.id,
            });
            return false;
        }

        try {
            await CommunicationLogBundles.setRecordLabels({
                parentDocPath: readyParentDocPath,
                bundleDocId,
                recordId: record.id,
                labelIds,
            });
            return true;
        } catch (error) {
            console.error('CommunicationLogUI: Failed saving communication log labels:', {
                error,
                ...persistenceDebugContext,
                recordId: record.id,
                bundleDocId,
            });
            messageApi.error('Failed to change labels. The entry is unchanged.');
            return false;
        }
    }, [
        ensureCommunicationLogBundlesReady,
        messageApi,
        persistenceDebugContext,
        recordBundleDocIdByRecordId,
        reportBlockedCommunicationLogAction,
    ]);

    const handleUploadEmbeddedAttachment: ParentUploadFunction = useCallback(async (
        fileToUpload: RcFile,
        emailRecordId: string,
    ) => {
        if (!parentUploadFilesFromHook || !parentHookArtifactPath) {
            console.error('CommunicationLogUI: Parent upload function or artifact path unavailable.');
            messageApi.error('Cannot upload embedded file: Configuration missing.');
            return null;
        }
        try {
            const results: {
                fileUid: string;
                versionId: string;
                versionData: AttachmentVersion;
            }[] = await parentUploadFilesFromHook(
                [fileToUpload],
                emailRecordId,
            );
            if (results?.length) {
                return results[0];
            }
            messageApi.warning(`Embedded file ${fileToUpload.name} uploaded, but details missing.`);
            return null;
        } catch (error) {
            console.error(`CommunicationLogUI: Failed uploading embedded attachment ${fileToUpload.name}`, error);
            messageApi.error(`Failed to upload embedded file: ${fileToUpload.name}`);
            return null;
        }
    }, [parentUploadFilesFromHook, parentHookArtifactPath, messageApi]);

    const handleEmailParsed = useCallback((emailData: EmailCommunicationRecord) => {
        if (recordCount >= MAX_COMMUNICATION_LOG_ENTRIES) {
            messageApi.error(`Maximum communication log entries reached (${MAX_COMMUNICATION_LOG_ENTRIES}). Please delete some entries before importing emails.`);
            return;
        }

        const fingerprint = getEmailFingerprint(emailData);
        if (existingEmailFingerprints.has(fingerprint) || pendingEmailFingerprints.current.has(fingerprint)) {
            messageApi.warning('This email has already been added to the communication log.');
            return;
        }

        const ready = ensureCommunicationLogBundlesReady({
            actionLabel: 'import email',
            logLabel: 'import email',
            recordId: emailData.id,
        });
        if (!ready.ok) {
            return;
        }

        pendingEmailFingerprints.current.add(fingerprint);

        void CommunicationLogBundles.appendRecord({
            parentDocPath: ready.parentDocPath,
            fieldId: communicationLogFieldId,
            record: emailData,
        }).then(() => {
            messageApi.success('Email imported.');
        }).catch((error) => {
            console.error('CommunicationLogUI: Failed importing email into bundles:', error);
            messageApi.error('Failed to import email.');
        }).finally(() => {
            pendingEmailFingerprints.current.delete(fingerprint);
        });
    }, [
        communicationLogFieldId,
        ensureCommunicationLogBundlesReady,
        existingEmailFingerprints,
        messageApi,
        recordCount,
    ]);

    const handleDeleteRecord = useCallback(async (record: CommunicationRecord, deleteAttachments: boolean) => {
        const ready = ensureCommunicationLogBundlesReady({
            actionLabel: 'delete this entry',
            logLabel: 'delete record',
            recordId: record.id,
        });
        if (!ready.ok) {
            return;
        }

        const bundleDocId = recordBundleDocIdByRecordId.get(record.id);
        if (!bundleDocId) {
            reportBlockedCommunicationLogAction({
                logLabel: 'delete record (missing bundleDocId)',
                userMessage: 'Cannot delete this entry yet. Please refresh and try again.',
                toast: 'error',
                recordId: record.id,
            });
            return;
        }

        if (record.type === 'email' && deleteAttachments) {
            if (!parentRemoveFileFromHook && !parentRemoveVersionFromHook) {
                messageApi.error('Cannot delete attachments: removal service unavailable.');
                return;
            }
            messageApi.loading({ content: 'Deleting email attachments...', key: 'deleteAttachments' });
            try {
                const attachmentDeletionPromises = Object.entries(record.attachments || {}).map(async ([fileUid, fileData]) => {
                    const versionsToDelete = fileData.versions || {};
                    const versionIds = Object.keys(versionsToDelete);

                    for (const versionId of versionIds) {
                        const versionDetails = versionsToDelete[versionId];
                        const fileName = versionDetails.versionFileName || fileData.fileName || `attachment_${fileUid}_${versionId}`;
                        if (parentRemoveVersionFromHook) {
                            await parentRemoveVersionFromHook(fileUid, versionId, fileName);
                        } else if (parentRemoveFileFromHook) {
                            await parentRemoveFileFromHook(fileUid, fileName);
                        } else {
                            throw new Error('Attachment removal functions unavailable.');
                        }
                    }
                });
                await Promise.all(attachmentDeletionPromises);
                messageApi.success({ content: 'Email attachments deleted.', key: 'deleteAttachments', duration: 2 });
            } catch (error) {
                console.error(`CommunicationLogUI: Attachment deletion failed for email ${record.id}`, error);
                messageApi.error({ content: 'Failed to delete attachments. Email not deleted.', key: 'deleteAttachments', duration: 3 });
                return;
            }
        }

        if (record.type === 'email' && parentContextAttachments && parentRemoveFileFromHook) {
            const failedOriginFiles: string[] = [];
            const embeddedFileUids = new Set(Object.keys(record.attachments || {}));
            for (const [fileUid, fileData] of Object.entries(parentContextAttachments)) {
                if (embeddedFileUids.has(fileUid)) continue;
                const isOriginFile = Object.values(fileData.versions || {}).some(
                    (version) => version.emailOriginRecordId === record.id,
                );
                if (isOriginFile) {
                    try {
                        await parentRemoveFileFromHook(fileUid, fileData.fileName || fileUid);
                    } catch (error) {
                        console.warn(`CommunicationLogUI: Failed to remove original email file ${fileData.fileName}`, error);
                        failedOriginFiles.push(fileData.fileName || fileUid);
                    }
                }
            }
            if (failedOriginFiles.length > 0) {
                messageApi.warning(`Could not remove origin file(s): ${failedOriginFiles.join(', ')}. You may need to delete them manually.`);
            }
        }

        try {
            await CommunicationLogBundles.removeRecord({
                parentDocPath: ready.parentDocPath,
                fieldId: communicationLogFieldId,
                recordId: record.id,
                bundleDocId,
            });
            messageApi.success(record.type === 'phone' ? 'Phone call deleted.' : 'Email deleted.');
        } catch (error) {
            console.error('CommunicationLogUI: Failed deleting communication record:', error);
            messageApi.error('Failed to delete communication entry.');
        }
    }, [
        communicationLogFieldId,
        ensureCommunicationLogBundlesReady,
        messageApi,
        parentContextAttachments,
        parentRemoveFileFromHook,
        parentRemoveVersionFromHook,
        recordBundleDocIdByRecordId,
        reportBlockedCommunicationLogAction,
    ]);

    const handleCheckOrderStatus = useCallback(async (
        record: CommunicationRecord,
    ): Promise<SignifyOrderStatusCheck | undefined> => {
        const ready = ensureCommunicationLogBundlesReady({
            actionLabel: 'check this order with Signify',
            logLabel: 'check order status',
            recordId: record.id,
        });
        if (!ready.ok) {
            return undefined;
        }

        if (!signifyCheckParent) {
            reportBlockedCommunicationLogAction({
                logLabel: 'check order status (missing parent)',
                userMessage: buildUnsavedParentMessage('check this order with Signify'),
                toast: 'error',
                recordId: record.id,
            });
            return undefined;
        }

        try {
            return await checkSignifyOrderStatus({
                parent: signifyCheckParent,
                fieldId: communicationLogFieldId,
                recordId: record.id,
            });
        } catch (error) {
            console.error('CommunicationLogUI: Failed checking order status with Signify:', {
                error,
                ...persistenceDebugContext,
                recordId: record.id,
            });
            messageApi.error('Could not ask Signify about this order. Nothing was changed.');
            return undefined;
        }
    }, [
        buildUnsavedParentMessage,
        communicationLogFieldId,
        ensureCommunicationLogBundlesReady,
        messageApi,
        persistenceDebugContext,
        reportBlockedCommunicationLogAction,
        signifyCheckParent,
    ]);

    const parentDocId = insideFormContext
        ? versionId
        : insideEntityContext
            ? selectedEntityRecord?.docId
            : undefined;

    return (
        <FlexCol>
            {!isPdfExportMode && (
                <LogTableControls
                    disabled={disabled}
                    onAddEmail={handleAddEmail}
                    onAddPhone={handleAddPhone}
                    onEmailParsed={handleEmailParsed}
                    uploadAttachmentFunction={handleUploadEmbeddedAttachment as ParentUploadFunction}
                    parentDocId={parentDocId}
                />
            )}
            <CommunicationTable
                {...props}
                records={filteredRecords}
                loading={bundlesEnabled && bundlesLoading}
                disabled={disabled}
                pdfExportMode={isPdfExportMode}
                logdisplay={logdisplay}
                onDelete={handleDeleteRecord}
                onEmailSave={handleUpdateEmail}
                onPhoneSave={handleUpdatePhone}
                onLabelsSave={handleLabelsSave}
                onCheckOrderStatus={handleCheckOrderStatus}
                labelSet={communicationLogLabels}
            />
        </FlexCol>
    );
};

export default CommunicationLogUI;
